/**
 * Runtime configuration loader.
 *
 * Config is delivered as a static /config.json file next to the built SPA so
 * that a single build artifact can be promoted across environments. Values are
 * NOT baked into the bundle at build time.
 *
 * Security note (MVP Option C): apiKey here is browser-visible after deploy and
 * must be treated as non-secret. See README / docs/deployment-notes.md.
 */

export interface RuntimeConfig {
  apiBaseUrl: string;
  apiKey: string;
  apiKeyHeaderName: string;
  apiKeyHeaderFormat: string;
  enabled: boolean;
  environment: string;
  allowedPrefillOrigins: string[];
  clientAppName: string;
  telemetryEnabled: boolean;
}

export class ConfigError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "ConfigError";
  }
}

export const DEFAULT_CONFIG_URL = "/config.json";
export const LOCAL_CONFIG_URL = "/config.local.json";

export interface LoadConfigOptions {
  url?: string;
  localUrl?: string;
  preferLocal?: boolean;
}

function isStringArray(value: unknown): value is string[] {
  return Array.isArray(value) && value.every((item) => typeof item === "string");
}

/**
 * Validates and narrows an untrusted parsed JSON object into a RuntimeConfig.
 * Throws ConfigError with an actionable message when required fields are
 * missing or malformed.
 */
export function parseRuntimeConfig(raw: unknown): RuntimeConfig {
  if (typeof raw !== "object" || raw === null) {
    throw new ConfigError("config.json must contain a JSON object.");
  }

  const obj = raw as Record<string, unknown>;

  const apiBaseUrl = obj.apiBaseUrl;
  if (typeof apiBaseUrl !== "string" || apiBaseUrl.trim() === "") {
    throw new ConfigError("config.json is missing required 'apiBaseUrl'.");
  }

  const apiKeyHeaderName =
    typeof obj.apiKeyHeaderName === "string" && obj.apiKeyHeaderName.trim() !== ""
      ? obj.apiKeyHeaderName
      : "Authorization";

  const apiKeyHeaderFormat =
    typeof obj.apiKeyHeaderFormat === "string" && obj.apiKeyHeaderFormat.trim() !== ""
      ? obj.apiKeyHeaderFormat
      : "ApiKey Key={key}";

  return {
    apiBaseUrl: apiBaseUrl.replace(/\/+$/, ""),
    apiKey: typeof obj.apiKey === "string" ? obj.apiKey : "",
    apiKeyHeaderName,
    apiKeyHeaderFormat,
    enabled: obj.enabled !== false,
    environment: typeof obj.environment === "string" ? obj.environment : "unknown",
    allowedPrefillOrigins: isStringArray(obj.allowedPrefillOrigins)
      ? obj.allowedPrefillOrigins
      : [],
    clientAppName:
      typeof obj.clientAppName === "string" && obj.clientAppName.trim() !== ""
        ? obj.clientAppName
        : "loandepot-turbovets-spa",
    telemetryEnabled: obj.telemetryEnabled === true,
  };
}

/**
 * Fetches and parses the runtime config. Any network, JSON-parse, or
 * validation failure is surfaced as a ConfigError so callers can render a
 * single configuration-error state.
 */
async function fetchRuntimeConfig(fetchFn: typeof fetch, url: string): Promise<RuntimeConfig> {
  let response: Response;
  try {
    response = await fetchFn(url, { cache: "no-store" });
  } catch (err) {
    throw new ConfigError(
      `Unable to load configuration from ${url}: ${(err as Error).message}`,
    );
  }

  if (!response.ok) {
    throw new ConfigError(
      `Unable to load configuration from ${url}: HTTP ${response.status}.`,
    );
  }

  let raw: unknown;
  try {
    raw = await response.json();
  } catch {
    throw new ConfigError(`Configuration at ${url} is not valid JSON.`);
  }

  return parseRuntimeConfig(raw);
}

function normalizeOptions(options: string | LoadConfigOptions): Required<LoadConfigOptions> {
  if (typeof options === "string") {
    return { url: options, localUrl: LOCAL_CONFIG_URL, preferLocal: false };
  }

  return {
    url: options.url ?? DEFAULT_CONFIG_URL,
    localUrl: options.localUrl ?? LOCAL_CONFIG_URL,
    preferLocal: options.preferLocal ?? import.meta.env.DEV,
  };
}

export async function loadConfig(
  fetchFn: typeof fetch = fetch,
  options: string | LoadConfigOptions = {},
): Promise<RuntimeConfig> {
  const normalized = normalizeOptions(options);

  if (normalized.preferLocal && normalized.url === DEFAULT_CONFIG_URL) {
    try {
      return await fetchRuntimeConfig(fetchFn, normalized.localUrl);
    } catch (err) {
      if (!(err instanceof ConfigError) || !err.message.includes("HTTP 404")) {
        throw err;
      }
    }
  }

  return fetchRuntimeConfig(fetchFn, normalized.url);
}

/**
 * Formats the API key header value using the configured template. Supports the
 * {key} placeholder, e.g. "ApiKey Key={key}" or "Bearer {key}".
 */
export function formatApiKeyHeader(config: RuntimeConfig): string {
  if (config.apiKeyHeaderFormat.includes("{key}")) {
    return config.apiKeyHeaderFormat.replace("{key}", () => config.apiKey);
  }
  // No placeholder: treat the format as a prefix (e.g. "ApiKey ").
  return `${config.apiKeyHeaderFormat}${config.apiKey}`;
}
